import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useConstants } from '@/contexts/ConstantsContext';
import {
  ArrowLeft,
  Phone,
  Mail,
  Globe,
  Linkedin,
  MapPin,
  Building,
  Calendar,
  Clock,
  AlertTriangle,
  Trash2,
  Edit,
  ExternalLink,
  Copy,
  MessageSquare,
  User,
  History,
  Lock,
  Archive,
  Star,
  CheckCircle,
  XCircle,
  Target,
  Video,
  Send,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import QuickLogModal from '@/components/QuickLogModal';
import SetFollowUpModal from '@/components/SetFollowUpModal';
import EditLeadModal from '@/components/EditLeadModal';
import BookMeetingModal from '@/components/BookMeetingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStageClass = (stage) => {
  const stageMap = {
    'Prospecting': 'stage-prospecting',
    'Appointment Scheduled': 'stage-appointment',
    'Qualified To Buy': 'stage-qualified',
    'Presentation Scheduled': 'stage-presentation',
    'Decision Maker Bought-In': 'stage-decision',
    'Contract Sent': 'stage-contract',
    'Closed Won': 'stage-won',
    'Closed Lost': 'stage-lost',
  };
  return stageMap[stage] || 'stage-prospecting';
};

const activityIcons = {
  'Cold Call': Phone,
  'Callback': Phone,
  'WhatsApp': MessageSquare,
  'LinkedIn Message': Linkedin,
  'SMS': MessageSquare,
  'Email (manual)': Mail,
  'Note': MessageSquare,
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isAdmin, isSetter, user } = useAuth();
  const { pipeline_stages, all_pipeline_stages, closed_lost_reasons, setter_allowed_stages, setter_locked_stages, appointment_status_options } = useConstants();

  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('activity');
  
  // Appointment status editing
  const [savingAppointment, setSavingAppointment] = useState(false);
  
  // Quality score editing
  const [savingQuality, setSavingQuality] = useState(false);
  
  // Meeting details editing
  const [savingMeetingDetails, setSavingMeetingDetails] = useState(false);
  
  // Zoom meeting booking
  const [bookMeetingOpen, setBookMeetingOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    fetchLead();
    fetchActivities();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}`);
      setLead(response.data);
      // Fetch history for admin
      if (isAdmin) {
        fetchHistory();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied: You can only view leads assigned to you');
      } else {
        toast.error('Failed to load lead');
      }
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities?lead_id=${id}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.put(`${API}/leads/${id}`, { stage: newStage });
      toast.success(`Stage updated to ${newStage}`);
      fetchLead();
      if (isAdmin) fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleArchive = async () => {
    try {
      await axios.put(`${API}/leads/${id}`, { archived: true });
      toast.success('Lead archived');
      navigate('/leads');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive lead');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!lead) return null;

  // Determine if this lead is read-only for setter
  const isReadOnlyForSetter = isSetter && (setter_locked_stages || []).includes(lead.stage);
  
  // Determine allowed stages for stage selector
  const allowedStages = isSetter ? (setter_allowed_stages || ['Prospecting', 'Appointment Scheduled']) : all_pipeline_stages;
  const displayStages = isAdmin ? all_pipeline_stages : pipeline_stages;

  // Handler for updating appointment status (admin only)
  const handleAppointmentStatusChange = async (newStatus) => {
    setSavingAppointment(true);
    try {
      await axios.put(`${API}/leads/${id}`, { appointment_status: newStatus });
      setLead({ ...lead, appointment_status: newStatus });
      toast.success(`Appointment status updated to "${newStatus}"`);
      if (isAdmin) fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appointment status');
    } finally {
      setSavingAppointment(false);
    }
  };

  // Handler for updating appointment notes
  const handleAppointmentNotesChange = async (notes) => {
    try {
      await axios.put(`${API}/leads/${id}`, { appointment_outcome_notes: notes });
      setLead({ ...lead, appointment_outcome_notes: notes });
      toast.success('Appointment notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  // Handler for updating meeting details
  const handleMeetingDetailChange = async (field, value) => {
    setSavingMeetingDetails(true);
    try {
      await axios.put(`${API}/leads/${id}`, { [field]: value });
      setLead({ ...lead, [field]: value });
      toast.success('Meeting details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save meeting details');
    } finally {
      setSavingMeetingDetails(false);
    }
  };

  // Handler for resending Zoom meeting email
  const handleResendEmail = async () => {
    setResendingEmail(true);
    try {
      await axios.post(`${API}/meetings/${id}/resend-email`);
      toast.success('Meeting confirmation email resent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingEmail(false);
    }
  };

  // Handler for updating quality score (admin only)
  const handleQualityChange = async (field, value) => {
    setSavingQuality(true);
    try {
      await axios.put(`${API}/leads/${id}`, { [field]: value });
      setLead({ ...lead, [field]: value });
      toast.success('Quality assessment saved');
      if (isAdmin) fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quality assessment');
    } finally {
      setSavingQuality(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">{lead.full_name}</h1>
              {lead.stale_lead && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Stale
                </Badge>
              )}
              {lead.do_not_call && (
                <Badge variant="destructive">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Do Not Call
                </Badge>
              )}
              {isReadOnlyForSetter && (
                <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/30">
                  <Lock className="w-3 h-3 mr-1" />
                  Read Only
                </Badge>
              )}
              {/* Local time display */}
              {lead.local_time && (
                <Badge 
                  variant="outline" 
                  className={lead.outside_call_hours ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : ''}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {lead.local_time} local
                  {lead.outside_call_hours && ' - Outside call hours'}
                </Badge>
              )}
              {/* DM Reached Badge (Admin only) */}
              {isAdmin && lead.dm_reached && (
                <Badge 
                  variant="outline" 
                  className="bg-blue-500/10 text-blue-400 border-blue-500/30"
                  data-testid="dm-reached-badge"
                >
                  <User className="w-3 h-3 mr-1" />
                  DM Reached
                  {lead.last_dm_reached_at && (
                    <span className="ml-1 text-xs opacity-70">
                      ({format(new Date(lead.last_dm_reached_at), 'MMM d')})
                    </span>
                  )}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">{lead.company_name}</p>
            {lead.timezone && (
              <p className="text-xs text-muted-foreground mt-1">Timezone: {lead.timezone}</p>
            )}
            {/* Last Updated By Info - prominent display */}
            {lead.last_updated_by_name && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="last-updated-info">
                <span className="text-primary/70">Last updated by:</span> {lead.last_updated_by_name}
                {lead.updated_at && (
                  <span className="ml-2">• {format(new Date(lead.updated_at), 'MMM d, h:mm a')}</span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isReadOnlyForSetter && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditModalOpen(true)}
              className="border-border"
              data-testid="edit-lead-btn"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="border-destructive text-destructive hover:bg-destructive/10"
              data-testid="archive-lead-btn"
            >
              <Archive className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Actions */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => setLogModalOpen(true)}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="log-activity-btn"
                  disabled={isReadOnlyForSetter}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Log Activity
                </Button>
                {/* Zoom Meeting Buttons */}
                {lead.zoom_join_url ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { navigator.clipboard.writeText(lead.zoom_join_url); toast.success('Zoom link copied'); }}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      data-testid="copy-zoom-link-btn"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Copy Zoom Link
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleResendEmail}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      disabled={resendingEmail}
                      data-testid="resend-zoom-email-btn"
                    >
                      {resendingEmail ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Resend Zoom Email
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setBookMeetingOpen(true)}
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    disabled={isReadOnlyForSetter}
                    data-testid="book-meeting-btn"
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Book Meeting + Zoom
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleCopy(lead.phone)}
                  className="border-border"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Phone
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setFollowUpModalOpen(true)}
                  className="border-border"
                  disabled={isReadOnlyForSetter}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Set Follow Up
                </Button>
                {lead.phone && (
                  <Button
                    variant="outline"
                    className="border-border"
                    onClick={() => window.open(`tel:${lead.phone}`)}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Next Follow Up */}
          <Card className={`bg-card ${lead.next_follow_up_date ? 'border-primary/30' : 'border-amber-500/30'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Next Follow Up
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lead.next_follow_up_date ? (
                <div>
                  <p className="text-2xl font-bold">{lead.next_follow_up_date}</p>
                  {lead.next_follow_up_time && (
                    <p className="text-muted-foreground">at {lead.next_follow_up_time}</p>
                  )}
                </div>
              ) : (
                <p className="text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  No follow up date set
                </p>
              )}
            </CardContent>
          </Card>

          {/* Activity Timeline + History Tabs */}
          <Card className="bg-card border-border">
            <CardHeader>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-secondary">
                  <TabsTrigger value="activity">Activity Timeline</TabsTrigger>
                  {isAdmin && <TabsTrigger value="history">History</TabsTrigger>}
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsContent value="activity" className="mt-0">
                  <ScrollArea className="h-[400px] pr-4">
                    {activities.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">No activities yet</p>
                    ) : (
                      <div className="space-y-4">
                        {activities.map((activity, index) => {
                          const Icon = activityIcons[activity.activity_type] || MessageSquare;
                          return (
                            <div key={activity.id} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                                  <Icon className="w-4 h-4 text-muted-foreground" />
                                </div>
                                {index < activities.length - 1 && (
                                  <div className="w-px h-full bg-border mt-2" />
                                )}
                              </div>
                              <div className="flex-1 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium">{activity.activity_type}</p>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                {activity.call_outcome && (
                                  <Badge variant="outline" className="mt-1 text-xs">
                                    {activity.call_outcome}
                                  </Badge>
                                )}
                                {activity.outcome_notes && (
                                  <p className="text-sm text-muted-foreground mt-2">
                                    {activity.outcome_notes}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  by {activity.created_by_name}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                {isAdmin && (
                  <TabsContent value="history" className="mt-0">
                    <ScrollArea className="h-[400px] pr-4">
                      {history.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No history yet</p>
                      ) : (
                        <div className="space-y-4">
                          {history.map((log, index) => (
                            <div key={log.id} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                                  <History className="w-4 h-4 text-muted-foreground" />
                                </div>
                                {index < history.length - 1 && (
                                  <div className="w-px h-full bg-border mt-2" />
                                )}
                              </div>
                              <div className="flex-1 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium capitalize">{log.action.replace('_', ' ')}</p>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(log.timestamp), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                {log.field_name && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    <span className="font-medium">{log.field_name}:</span>{' '}
                                    <span className="text-red-400 line-through">{log.old_value || 'None'}</span>
                                    {' → '}
                                    <span className="text-emerald-400">{log.new_value || 'None'}</span>
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  by {log.changed_by_name || 'System'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                )}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stage */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Stage</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={lead.stage}
                onValueChange={handleStageChange}
                disabled={isReadOnlyForSetter}
              >
                <SelectTrigger className={`bg-secondary border-border ${getStageClass(lead.stage)}`} data-testid="stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {displayStages.map(stage => (
                    <SelectItem
                      key={stage}
                      value={stage}
                      disabled={isSetter && !allowedStages.includes(stage)}
                    >
                      {stage}
                      {isSetter && !allowedStages.includes(stage) && ' 🔒'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {lead.stage === 'Closed Lost' && (
                <div className="mt-3">
                  <label className="text-xs text-muted-foreground">Reason</label>
                  <p className="text-sm">{lead.closed_lost_reason || 'Not specified'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Contact Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono">{lead.phone}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleCopy(lead.phone)}>
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              {lead.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="truncate">{lead.email}</span>
                </div>
              )}
              {lead.linkedin_url && (
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-muted-foreground" />
                  <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                    LinkedIn
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                </div>
              )}
              {lead.website && (
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                    Website
                    <ExternalLink className="w-3 h-3 inline ml-1" />
                  </a>
                </div>
              )}
              {lead.city_state && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{lead.city_state}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Market</span>
                <Badge variant="outline" className={lead.country_market === 'UK' ? 'badge-uk' : 'badge-usa'}>
                  {lead.country_market}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Industry</span>
                <span>{lead.industry}</span>
              </div>
              {lead.sector && lead.sector !== 'Other' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sector</span>
                  <span>{lead.sector}</span>
                </div>
              )}
              {lead.niche && lead.niche !== 'Other' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Niche</span>
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                    {lead.niche}
                  </Badge>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">ICP Fit</span>
                <Badge variant="outline" className={`${lead.icp_fit === 'Good' ? 'badge-good' : lead.icp_fit === 'Maybe' ? 'badge-maybe' : 'badge-bad'}`}>
                  {lead.icp_fit}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lead Source</span>
                <span>{lead.lead_source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Owner</span>
                <span>{lead.owner_name || 'Unassigned'}</span>
              </div>
              {lead.job_title && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Job Title</span>
                  <span>{lead.job_title}</span>
                </div>
              )}
              {lead.company_size && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company Size</span>
                  <span>{lead.company_size}</span>
                </div>
              )}
              {lead.deal_value_estimate && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deal Value</span>
                  <span className="font-semibold">£{lead.deal_value_estimate.toLocaleString()}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Calls</span>
                <span>{lead.total_call_attempts}</span>
              </div>
              {lead.last_call_outcome && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Last Outcome</span>
                  <span>{lead.last_call_outcome}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Best Time</span>
                <span>{lead.best_time_to_call}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Preferred Phone</span>
                <span>{lead.preferred_phone}</span>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {lead.notes && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{lead.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Meeting Details Panel - Show when appointment is scheduled */}
          {(lead.stage === 'Appointment Scheduled' || lead.appointment_status) && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Meeting Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.meeting_datetime && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Meeting Date</span>
                    <span>{new Date(lead.meeting_datetime).toLocaleString()}</span>
                  </div>
                )}
                {/* Zoom Meeting Info */}
                {lead.zoom_join_url && (
                  <div className="p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="zoom-meeting-info">
                    <div className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-1">
                      <Video className="w-4 h-4" />
                      Zoom Meeting
                    </div>
                    <a
                      href={lead.zoom_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:underline break-all"
                    >
                      {lead.zoom_join_url}
                    </a>
                    {lead.zoom_meeting_password && (
                      <p className="text-xs text-muted-foreground mt-1">Password: {lead.zoom_meeting_password}</p>
                    )}
                  </div>
                )}
                {/* Reschedule Tracking */}
                {lead.reschedule_count > 0 && (
                  <div className="p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="reschedule-info">
                    <div className="flex items-center gap-2 text-blue-400 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">Rescheduled {lead.reschedule_count}x</span>
                    </div>
                    {lead.previous_meeting_datetime && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Previous: {new Date(lead.previous_meeting_datetime).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Decision Maker Name</Label>
                    <Input
                      value={lead.decision_maker_name || ''}
                      onChange={(e) => setLead({ ...lead, decision_maker_name: e.target.value })}
                      onBlur={(e) => handleMeetingDetailChange('decision_maker_name', e.target.value)}
                      placeholder="Name"
                      className="bg-secondary border-border text-sm h-8"
                      disabled={savingMeetingDetails}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Role</Label>
                    <Input
                      value={lead.decision_maker_role || ''}
                      onChange={(e) => setLead({ ...lead, decision_maker_role: e.target.value })}
                      onBlur={(e) => handleMeetingDetailChange('decision_maker_role', e.target.value)}
                      placeholder="Owner, Manager, etc."
                      className="bg-secondary border-border text-sm h-8"
                      disabled={savingMeetingDetails}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Business Type</Label>
                  <Input
                    value={lead.business_type || ''}
                    onChange={(e) => setLead({ ...lead, business_type: e.target.value })}
                    onBlur={(e) => handleMeetingDetailChange('business_type', e.target.value)}
                    placeholder="Type of business"
                    className="bg-secondary border-border text-sm h-8"
                    disabled={savingMeetingDetails}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">DM Email</Label>
                    <Input
                      type="email"
                      value={lead.decision_maker_email || ''}
                      onChange={(e) => setLead({ ...lead, decision_maker_email: e.target.value })}
                      onBlur={(e) => handleMeetingDetailChange('decision_maker_email', e.target.value)}
                      placeholder="Email for Zoom invite"
                      className="bg-secondary border-border text-sm h-8"
                      disabled={savingMeetingDetails}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">DM Phone</Label>
                    <Input
                      value={lead.decision_maker_phone || ''}
                      onChange={(e) => setLead({ ...lead, decision_maker_phone: e.target.value })}
                      onBlur={(e) => handleMeetingDetailChange('decision_maker_phone', e.target.value)}
                      placeholder="If different from main"
                      className="bg-secondary border-border text-sm h-8"
                      disabled={savingMeetingDetails}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Meeting Notes</Label>
                  <Textarea
                    value={lead.meeting_notes || ''}
                    onChange={(e) => setLead({ ...lead, meeting_notes: e.target.value })}
                    onBlur={(e) => handleMeetingDetailChange('meeting_notes', e.target.value)}
                    placeholder="Any specific info for the meeting..."
                    className="bg-secondary border-border text-sm min-h-[60px]"
                    disabled={savingMeetingDetails}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Appointment Status Panel - Show when appointment is scheduled */}
          {(lead.stage === 'Appointment Scheduled' || lead.appointment_status) && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Appointment Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  {isAdmin ? (
                    <Select 
                      value={lead.appointment_status || 'Scheduled'} 
                      onValueChange={handleAppointmentStatusChange}
                      disabled={savingAppointment}
                    >
                      <SelectTrigger className="bg-secondary border-border" data-testid="appointment-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(appointment_status_options || ['Scheduled', 'Attended', 'No show', 'Rescheduled', 'Cancelled']).map(o => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={`${
                      lead.appointment_status === 'Attended' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                      lead.appointment_status === 'No show' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                      lead.appointment_status === 'Rescheduled' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                      lead.appointment_status === 'Cancelled' ? 'bg-gray-500/10 text-gray-400 border-gray-500/30' :
                      ''
                    }`}>
                      {lead.appointment_status || 'Scheduled'}
                    </Badge>
                  )}
                </div>
                {lead.appointment_booked_by_name && (
                  <div className="text-xs text-muted-foreground">
                    Booked by: {lead.appointment_booked_by_name}
                  </div>
                )}
                {isAdmin && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <Textarea
                      placeholder="Add outcome notes..."
                      value={lead.appointment_outcome_notes || ''}
                      onChange={(e) => setLead({ ...lead, appointment_outcome_notes: e.target.value })}
                      onBlur={(e) => handleAppointmentNotesChange(e.target.value)}
                      className="bg-secondary border-border text-sm min-h-[60px]"
                    />
                  </div>
                )}
                {!isAdmin && lead.appointment_outcome_notes && (
                  <div className="text-sm text-muted-foreground">
                    {lead.appointment_outcome_notes}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quality Score Panel - Admin can edit, Setter can view */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Star className="w-4 h-4" />
                Quality Assessment
                {!isAdmin && <Badge variant="outline" className="text-xs ml-2">View Only</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Quality Score (1-5)</Label>
                {isAdmin ? (
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(score => (
                      <Button
                        key={score}
                        variant={lead.quality_score === score ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleQualityChange('quality_score', score)}
                        disabled={savingQuality}
                        className={lead.quality_score === score ? '' : 'border-border'}
                        data-testid={`quality-score-${score}`}
                      >
                        {score}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {lead.quality_score ? (
                      <>
                        <span className="font-bold text-lg">{lead.quality_score}</span>
                        <span className="text-muted-foreground">/ 5</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not scored</span>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Good Fit?</Label>
                {isAdmin ? (
                  <div className="flex gap-2">
                    <Button
                      variant={lead.good_fit === true ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleQualityChange('good_fit', true)}
                      disabled={savingQuality}
                      className={lead.good_fit === true ? 'bg-emerald-600 hover:bg-emerald-600/90' : 'border-border'}
                      data-testid="good-fit-yes"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Yes
                    </Button>
                    <Button
                      variant={lead.good_fit === false ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleQualityChange('good_fit', false)}
                      disabled={savingQuality}
                      className={lead.good_fit === false ? 'bg-red-600 hover:bg-red-600/90' : 'border-border'}
                      data-testid="good-fit-no"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      No
                    </Button>
                  </div>
                ) : (
                  <div>
                    {lead.good_fit === true && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Yes
                      </Badge>
                    )}
                    {lead.good_fit === false && (
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/30">
                        <XCircle className="w-3 h-3 mr-1" />
                        No
                      </Badge>
                    )}
                    {lead.good_fit === null && (
                      <span className="text-muted-foreground text-sm">Not assessed</span>
                    )}
                  </div>
                )}
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Quality Notes</Label>
                  <Textarea
                    placeholder="Add quality assessment notes..."
                    value={lead.quality_notes || ''}
                    onChange={(e) => setLead({ ...lead, quality_notes: e.target.value })}
                    onBlur={(e) => handleQualityChange('quality_notes', e.target.value)}
                    className="bg-secondary border-border text-sm min-h-[60px]"
                  />
                </div>
              )}
              {!isAdmin && lead.quality_notes && (
                <div className="text-sm text-muted-foreground">
                  {lead.quality_notes}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Call Cadence Info */}
          {lead.call_attempt_number > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Call Cadence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Attempt</span>
                  <Badge variant={lead.call_attempt_number >= 5 ? 'destructive' : 'outline'} className={lead.call_attempt_number < 5 ? 'border-border' : ''}>
                    {lead.call_attempt_number} / 6
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Recommended</span>
                  <span className="text-sm font-medium text-primary">{lead.recommended_action}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modals */}
      <QuickLogModal
        open={logModalOpen}
        onOpenChange={setLogModalOpen}
        leadId={lead.id}
        lead={lead}
        onSuccess={() => { fetchLead(); fetchActivities(); if (isAdmin) fetchHistory(); }}
      />

      <SetFollowUpModal
        open={followUpModalOpen}
        onOpenChange={setFollowUpModalOpen}
        lead={lead}
        onSuccess={fetchLead}
      />

      <EditLeadModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        lead={lead}
        onSuccess={() => { fetchLead(); if (isAdmin) fetchHistory(); }}
      />

      <BookMeetingModal
        open={bookMeetingOpen}
        onOpenChange={setBookMeetingOpen}
        lead={lead}
        onSuccess={() => { fetchLead(); fetchActivities(); if (isAdmin) fetchHistory(); }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive {lead.full_name}? The lead can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-destructive hover:bg-destructive/90">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadDetail;
